// ============================================================================
// useMapData — single fetch of the WS7 map data contract.
//
// House pattern: useEffect loader + shared supabase client (the app does not
// use TanStack Query). Reads v_map_facilities (one row per geocoded facility,
// providers pre-aggregated — never join assignments here, that reintroduces
// the FAC-0162 fan-out) and v_map_offices. Closed facilities ARE fetched;
// visibility is a toggle concern (WS10), not a data concern.
// ============================================================================

import { useEffect, useState } from 'react';
import { supabase } from '../supabase';
import { errorMessage } from '../errors';
import type { MapFacility, MapOffice } from './map-types';

interface MapData {
  facilities: MapFacility[];
  offices: MapOffice[];
  loading: boolean;
  error: string | null;
}

export function useMapData(): MapData {
  const [facilities, setFacilities] = useState<MapFacility[]>([]);
  const [offices, setOffices] = useState<MapOffice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // PostgREST caps selects at 1000 — facilities are ~230, one page is
        // fine, but keep the explicit range so growth never silently clips.
        const [fRes, oRes] = await Promise.all([
          supabase.from('v_map_facilities').select('*').range(0, 999),
          supabase.from('v_map_offices').select('*').range(0, 99),
        ]);
        if (fRes.error) throw fRes.error;
        if (oRes.error) throw oRes.error;
        if (cancelled) return;
        setFacilities((fRes.data ?? []) as MapFacility[]);
        setOffices((oRes.data ?? []) as MapOffice[]);
        setError(null);
      } catch (e) {
        if (!cancelled) setError(errorMessage(e, 'Failed to load map data.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return { facilities, offices, loading, error };
}

/** Fetch the runtime Maps JS SDK browser key (vault-backed; '' = not set). */
export function useMapsBrowserKey(): { key: string | null; keyLoading: boolean } {
  const [key, setKey] = useState<string | null>(null);
  const [keyLoading, setKeyLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase.rpc('get_maps_browser_key');
      if (cancelled) return;
      setKey(error ? '' : ((data as string | null) ?? ''));
      setKeyLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return { key, keyLoading };
}
